import React, { useState, useEffect, useRef } from 'react';
import { Container } from 'react-bootstrap';
import axios from 'axios';
import Quill from 'quill';
import { createApiUrl } from '../utils/apiConfig';
import DragDropFileUpload from '../components/DragDropFileUpload';
import StandardPagination from '../components/StandardPagination';
import 'quill/dist/quill.snow.css';
import './NewsAdmin.css';

const NewsAdmin = () => {
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editingArticle, setEditingArticle] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    category: '',
    author: '',
    readTime: '',
    images: [],
    coverImage: '',
    published: false
  });
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  const [linkModal, setLinkModal] = useState({ show: false, url: '', text: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const [articlesPerPage, setArticlesPerPage] = useState(12); // 4 rows x 3 columns
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteModal, setDeleteModal] = useState({ show: false, articleId: null, articleTitle: '' });
  const [imageDeleteModal, setImageDeleteModal] = useState({ show: false, imagePath: '', imageIndex: null, isNewFile: false });
  const [insertedImages, setInsertedImages] = useState(new Set());
  const editorRef = useRef(null);
  const quillRef = useRef(null);

  useEffect(() => {
    // Load articles and categories from database
    const fetchData = async () => {
      try {
        const [articlesResponse, categoriesResponse] = await Promise.all([
          axios.get(createApiUrl('/api/news/articles'), { withCredentials: true }),
          axios.get(createApiUrl('/api/news/categories'), { withCredentials: true })
        ]);
        setArticles(articlesResponse.data);
        setCategories(categoriesResponse.data);
      } catch (error) {
        console.error('Error fetching data:', error);
        setArticles([]);
        setCategories([]);
      }
    };
    
    fetchData();
  }, []);

  useEffect(() => {
    if (showForm) {
      // Initialize Quill after a longer delay to ensure DOM is fully ready
      setTimeout(() => {
        initializeQuill();
      }, 300);
    } else {
      // Clean up Quill when modal closes
      if (quillRef.current) {
        quillRef.current = null;
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- initializeQuill intentionally excluded
  }, [showForm]);

  const _saveArticles = async (newArticles) => {
    setArticles(newArticles);
  };

  const handleFileUpload = async (files, category) => {
    if (!files || files.length === 0) return [];
    
    setUploading(true);
    try {
      const formData = new FormData();
      files.forEach(file => {
        formData.append('media', file);
      });
      formData.append('articleId', editingArticle ? editingArticle._id : 'temp');
      formData.append('category', category);
      
      const response = await axios.post(createApiUrl('/api/news/upload-media'), formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        withCredentials: true
      });
      
      setUploading(false);
      
      // If we're editing an article, refresh the editingArticle with updated images
      if (editingArticle) {
        try {
          const articleResponse = await axios.get(createApiUrl(`/api/news/articles/${editingArticle._id}`), {
            withCredentials: true
          });
          setEditingArticle(articleResponse.data);
        } catch (refreshError) {
          console.error('Error refreshing article:', refreshError);
        }
      }
      
      return response.data.media.map(item => item.url);
    } catch (error) {
      console.error('Error uploading media:', error);
      setUploading(false);
      alert('Error uploading media. Please try again.');
      return [];
    }
  };

  const insertLink = () => {
    if (linkModal.url.trim()) {
      const linkText = linkModal.text.trim() || linkModal.url;
      const linkHtml = `<a href="${linkModal.url}" target="_blank" rel="noopener noreferrer">${linkText}</a>`;
      
      // Insert the link at cursor position
      const selection = window.getSelection();
      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const linkElement = document.createElement('div');
        linkElement.innerHTML = linkHtml;
        range.insertNode(linkElement.firstChild);
        range.collapse(false);
      }
      
      setLinkModal({ show: false, url: '', text: '' });
    }
  };

  const insertMediaIntoEditor = (file) => {
    const quill = quillRef.current;
    if (quill) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const range = quill.getSelection();
        
        if (file.type.startsWith('video/')) {
          // Insert video
          const videoHtml = `<video controls controlsList="nodownload noplaybackrate noremoteplayback" disablePictureInPicture style="max-width: 100%; height: auto;">
            <source src="${e.target.result}" type="${file.type}">
            Your browser does not support the video tag.
          </video>`;
          quill.clipboard.dangerouslyPasteHTML(range.index, videoHtml);
          quill.setSelection(range.index + 1);
        } else {
          // Insert image
          quill.insertEmbed(range.index, 'image', e.target.result);
          quill.setSelection(range.index + 1);
        }
        
        // Track that this media was inserted
        setInsertedImages(prev => new Set([...prev, file.name]));
      };
      reader.readAsDataURL(file);
    }
  };

  const insertExistingImageIntoEditor = (imagePath) => {
    const quill = quillRef.current;
    if (quill) {
      const range = quill.getSelection();
      quill.insertEmbed(range.index, 'image', imagePath);
      quill.setSelection(range.index + 1);
      // Track that this image was inserted
      setInsertedImages(prev => new Set([...prev, imagePath]));
    }
  };

  const deleteSelectedFile = (index) => {
    const newSelectedFiles = selectedFiles.filter((_, i) => i !== index);
    setSelectedFiles(newSelectedFiles);
    
    // Update formData images array to match the remaining selected files
    const newImages = newSelectedFiles.map(file => file.name);
    setFormData({...formData, images: newImages});
  };

  // File handlers for drag-drop component
  const handleFileSelect = (files) => {
    // Handle both single file and file array
    const fileArray = Array.isArray(files) ? files : [files];
    setSelectedFiles(prevFiles => [...prevFiles, ...fileArray]);
  };

  const handleFileRemove = () => {
    setSelectedFiles([]);
  };

  // Show delete confirmation modal for new files
  const showDeleteModalForNewFile = (index) => {
    const file = selectedFiles[index];
    setImageDeleteModal({
      show: true,
      imagePath: file?.name || 'Unknown file',
      imageIndex: index,
      isNewFile: true
    });
  };

  // Show delete confirmation modal for existing images
  const showDeleteModalForExistingImage = (imagePath, index) => {
    // Extract original filename from S3 URL
    const urlParts = imagePath.split('/');
    const fullFilename = urlParts[urlParts.length - 1];
    // Remove timestamp prefix (format: timestamp-originalname)
    const filename = fullFilename.includes('-') ? fullFilename.split('-').slice(1).join('-') : fullFilename;
    
    setImageDeleteModal({
      show: true,
      imagePath: filename || 'Unknown image',
      imageIndex: index,
      isNewFile: false
    });
  };

  // Remove selected file from the array (no API call needed)
  const removeSelectedFile = (index) => {
    setSelectedFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
    setImageDeleteModal({ show: false, imagePath: '', imageIndex: null, isNewFile: false });
  };

  // Delete existing image from article and S3
  const deleteExistingImage = async () => {
    const { imagePath, imageIndex } = imageDeleteModal;
    
    try {
      // Extract filename from the full path
      const filename = imagePath.split('/').pop();
      
      // Call the delete API
      const response = await axios.delete(`/api/news/delete-media/${filename}`, {
        withCredentials: true
      });

      if (response.status === 200) {
        // Remove from the article's images array
        if (editingArticle) {
          const newImages = [...(editingArticle.images || [])];
          newImages.splice(imageIndex, 1);
          
          // Update the article in the articles list
          setArticles(prevArticles => 
            prevArticles.map(article => 
              article._id === editingArticle._id 
                ? { ...article, images: newImages }
                : article
            )
          );
          
          // Update the editing article
          setEditingArticle(prev => ({ ...prev, images: newImages }));
        }
        
        setNotification({
          show: true,
          message: 'Image deleted successfully from article and storage',
          type: 'success'
        });
      }
    } catch (error) {
      console.error('Error deleting image:', error);
      setNotification({
        show: true,
        message: 'Failed to delete image',
        type: 'error'
      });
    } finally {
      setImageDeleteModal({ show: false, imagePath: '', imageIndex: null, isNewFile: false });
    }
  };

  // Cancel delete operation
  const cancelImageDelete = () => {
    setImageDeleteModal({ show: false, imagePath: '', imageIndex: null, isNewFile: false });
  };

  const initializeQuill = () => {
    
    if (editorRef.current && !quillRef.current) {
      
      const toolbarOptions = [
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'header': [1, 2, 3, false] }],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        [{ 'script': 'sub'}, { 'script': 'super' }],
        [{ 'indent': '-1'}, { 'indent': '+1' }],
        [{ 'direction': 'rtl' }],
        [{ 'size': ['small', false, 'large', 'huge'] }],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'font': [] }],
        [{ 'align': [] }],
        ['blockquote', 'code-block'],
        ['link', 'image'],
        ['clean']
      ];

      try {
        quillRef.current = new Quill(editorRef.current, {
          theme: 'snow',
          modules: {
            toolbar: {
              container: toolbarOptions,
              handlers: {
                image: () => {
                  const input = document.createElement('input');
                  input.setAttribute('type', 'file');
                  input.setAttribute('accept', 'image/*');
                  input.click();

                  input.onchange = () => {
                    const file = input.files[0];
                    if (file) {
                      const reader = new FileReader();
                      reader.onload = () => {
                        const range = quillRef.current.getSelection();
                        quillRef.current.insertEmbed(range.index, 'image', reader.result);
                      };
                      reader.readAsDataURL(file);
                    }
                  };
                }
              }
            }
          },
          placeholder: 'Write your article content here...'
        });


        // Set initial content if editing
        if (formData.content) {
          quillRef.current.root.innerHTML = formData.content;
        }

        // Listen for content changes
        quillRef.current.on('text-change', () => {
          const content = quillRef.current.root.innerHTML;
          setFormData(prevData => ({...prevData, content: content}));
        });
      } catch (error) {
        console.error('Error initializing Quill:', error);
      }
    } else {
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    let imagePaths = [];
    let finalContent = formData.content;
    let uploadedPaths = [];
    
    // Upload files if selected
    if (selectedFiles.length > 0) {
      uploadedPaths = await handleFileUpload(selectedFiles, formData.category);
      if (uploadedPaths.length === 0) {
        return; // Stop if upload failed
      }
      
      // Combine existing images with new ones
      const existingImages = editingArticle ? (editingArticle.images || (editingArticle.image ? [editingArticle.image] : [])) : [];
      imagePaths = [...existingImages, ...uploadedPaths];
      
      // Add cover image to the images array if it's selected
      if (formData.coverImage) {
        // If cover image is a filename from selectedFiles, find its uploaded URL
        const selectedFile = selectedFiles.find(f => f.name === formData.coverImage);
        if (selectedFile) {
          // Find the uploaded URL for this file
          const fileIndex = selectedFiles.findIndex(f => f.name === formData.coverImage);
          const uploadedUrl = uploadedPaths[fileIndex];
          if (uploadedUrl && !imagePaths.includes(uploadedUrl)) {
            imagePaths.push(uploadedUrl);
          }
          // Update coverImage to use the uploaded URL
          formData.coverImage = uploadedUrl;
        } else if (!imagePaths.includes(formData.coverImage)) {
          // If it's an existing image URL, add it to the array
          imagePaths.push(formData.coverImage);
        }
      }
      
      // Ensure cover image is also uploaded to S3 if it's a new file
      if (formData.coverImage && selectedFiles.find(f => f.name === formData.coverImage)) {
        const _coverFile = selectedFiles.find(f => f.name === formData.coverImage);
        const coverFileIndex = selectedFiles.findIndex(f => f.name === formData.coverImage);
        const coverUploadedUrl = uploadedPaths[coverFileIndex];
        
        if (coverUploadedUrl) {
          formData.coverImage = coverUploadedUrl;
        }
      }
      
      // Add only unused images as a carousel at the bottom of the article
      if (imagePaths.length > 0) {
        // Filter out images that were inserted into the content
        const unusedImages = imagePaths.filter(imagePath => {
          // Check if this image path corresponds to a file that wasn't inserted
          const fileName = imagePath.split('/').pop(); // Get filename from path
          return !insertedImages.has(fileName);
        });
        
        if (unusedImages.length > 0) {
          const carouselHTML = `
            <div style="margin-top: 30px; padding-top: 20px; border-top: 2px solid var(--lightOrange);">
              <h3 style="color: var(--darkOrange); margin-bottom: 20px; font-family: var(--primaryFont);">Article Gallery</h3>
              <div style="display: flex; overflow-x: auto; gap: 15px; padding: 10px 0; scrollbar-width: thin; scrollbar-color: var(--lightOrange) transparent;">
                ${unusedImages.map(imagePath => `
                  <div style="flex: 0 0 auto; min-width: 200px; max-width: 300px;">
                    <img src="${imagePath}" alt="Article image" style="width: 100%; height: 200px; object-fit: cover; border-radius: 8px; border: 2px solid var(--lightOrange); box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);" />
                  </div>
                `).join('')}
              </div>
            </div>
          `;
          finalContent += carouselHTML;
        }
      }
    } else {
      // If no new files, keep existing images
      imagePaths = editingArticle ? (editingArticle.images || (editingArticle.image ? [editingArticle.image] : [])) : [];
      
      // Add cover image to the images array if it's selected and not already included
      if (formData.coverImage && !imagePaths.includes(formData.coverImage)) {
        imagePaths.push(formData.coverImage);
      }
    }
    
    // Prepare uploaded images data for new articles
    let uploadedImagesData = [];
    if (!editingArticle && selectedFiles.length > 0) {
      // This will be used to create NewsImage records in the database
      uploadedImagesData = selectedFiles.map((file, index) => {
        // Find the uploaded URL for this specific file
        const uploadedUrl = uploadedPaths[index];
        return {
          filename: file.name,
          originalName: file.name,
          s3Key: `news-images/${formData.category}/${file.name}`,
          s3Url: uploadedUrl || '',
          mimeType: file.type,
          size: file.size
        };
      });
      
      // Ensure cover image is also included in uploadedImagesData if it's a new file
      if (formData.coverImage) {
        const coverFile = selectedFiles.find(f => f.name === formData.coverImage);
        if (coverFile) {
          const coverFileIndex = selectedFiles.findIndex(f => f.name === formData.coverImage);
          const coverUploadedUrl = uploadedPaths[coverFileIndex];
          
          // Check if cover image is already in uploadedImagesData
          const coverImageExists = uploadedImagesData.find(img => img.filename === formData.coverImage);
          if (!coverImageExists && coverUploadedUrl) {
            uploadedImagesData.push({
              filename: coverFile.name,
              originalName: coverFile.name,
              s3Key: `news-images/${formData.category}/${coverFile.name}`,
              s3Url: coverUploadedUrl,
              mimeType: coverFile.type,
              size: coverFile.size
            });
          }
        }
      }
    }

    const newArticle = {
      ...formData,
      content: finalContent,
      images: imagePaths,
      publishDate: formData.published ? new Date() : null,
      uploadedImages: uploadedImagesData
    };

    if (editingArticle) {
      // Update existing article in database
      try {
        const response = await axios.put(createApiUrl(`/api/news/articles/${editingArticle._id}`), newArticle, {
          withCredentials: true
        });
        
        // Update local state
        const updatedArticles = articles.map(article => 
          article._id === editingArticle._id ? response.data : article
        );
        setArticles(updatedArticles);
      } catch (error) {
        console.error('Error updating article:', error);
        alert('Error updating article. Please try again.');
        return;
      }
    } else {
      // Create new article in database
      try {
        const response = await axios.post(createApiUrl('/api/news/articles'), newArticle, {
          withCredentials: true
        });
        
        // Update local state
        setArticles([response.data, ...articles]);
      } catch (error) {
        console.error('Error creating article:', error);
        alert('Error creating article. Please try again.');
        return;
      }
    }

    setFormData({
      title: '',
      excerpt: '',
      content: '',
      category: 'announcement',
      author: '',
      readTime: '',
      images: [],
      coverImage: '',
      published: false
    });
    setSelectedFiles([]);
    setInsertedImages(new Set());
    setEditingArticle(null);
    setShowForm(false);
    
    // Show success notification
    if (newArticle.published) {
      setNotification({ 
        show: true, 
        message: 'Article published successfully and is now visible on the news page!', 
        type: 'success' 
      });
    } else {
      setNotification({ 
        show: true, 
        message: 'Article saved as draft. Use the "Publish" button to make it visible on the news page.', 
        type: 'warning' 
      });
    }
    
    // Auto-hide notification after 5 seconds
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 5000);
  };

  const handleEdit = (article) => {
    
    setEditingArticle(article);
    setFormData({
      title: article.title,
      excerpt: article.excerpt,
      content: article.content,
      category: article.category,
      author: article.author,
      readTime: article.readTime,
      images: article.images || (article.image ? [article.image] : []),
      coverImage: article.coverImage || '',
      published: article.published
    });
    setSelectedFiles([]);
    
    // Detect images already in the content and mark them as inserted
    const existingImages = article.images || (article.image ? [article.image] : []);
    const insertedSet = new Set();
    
    // Scan the content for existing images
    existingImages.forEach(imagePath => {
      if (article.content && article.content.includes(imagePath)) {
        const fileName = imagePath.split('/').pop();
        insertedSet.add(fileName);
      }
    });
    
    setInsertedImages(insertedSet);
    setShowForm(true);
  };

  const handleDelete = (id) => {
    const article = articles.find(a => a._id === id);
    setDeleteModal({ show: true, articleId: id, articleTitle: article.title });
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`/api/news/articles/${deleteModal.articleId}`, {
        withCredentials: true
      });
      
      // Update local state
      const updatedArticles = articles.filter(article => article._id !== deleteModal.articleId);
      setArticles(updatedArticles);
      setDeleteModal({ show: false, articleId: null, articleTitle: '' });
    } catch (error) {
      console.error('Error deleting article:', error);
      alert('Error deleting article. Please try again.');
    }
  };

  const cancelDelete = () => {
    setDeleteModal({ show: false, articleId: null, articleTitle: '' });
  };

  const togglePublished = async (id) => {
    try {
      const article = articles.find(a => a._id === id);
      const newPublishedStatus = !article.published;
      
      const response = await axios.put(`/api/news/articles/${id}`, {
        ...article,
        published: newPublishedStatus,
        publishDate: newPublishedStatus ? new Date() : null
      }, {
        withCredentials: true
      });
      
      // Update local state
      const updatedArticles = articles.map(article => 
        article._id === id ? response.data : article
      );
      setArticles(updatedArticles);
      
      // Show feedback message
      if (newPublishedStatus) {
        setNotification({ show: true, message: 'Article has been published and is now visible on the news page.', type: 'success' });
      } else {
        setNotification({ show: true, message: 'Article has been unpublished and removed from the news page.', type: 'warning' });
      }
      
      // Auto-hide notification after 3 seconds
      setTimeout(() => {
        setNotification({ show: false, message: '', type: '' });
      }, 3000);
    } catch (error) {
      console.error('Error toggling published status:', error);
      alert('Error updating article status. Please try again.');
    }
  };

  // Search and filtering functions
  const getFilteredArticles = () => {
    if (!searchQuery) return articles;
    return articles.filter(article => 
      article.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      article.excerpt?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      article.category?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      article.author?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  // Pagination logic
  const filteredArticles = getFilteredArticles();
  const indexOfLastArticle = currentPage * articlesPerPage;
  const indexOfFirstArticle = indexOfLastArticle - articlesPerPage;
  const currentArticles = filteredArticles.slice(indexOfFirstArticle, indexOfLastArticle);
  const totalPages = Math.ceil(filteredArticles.length / articlesPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1); // Reset to first page when searching
  };

  // Items per page handler for NewsAdmin
  const handleItemsPerPageChange = (newItemsPerPage) => {
    setArticlesPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  return (
    <div className="news-admin-page">
      <div className="news-admin-main-content">
        {/* Notification Popup */}
      {notification.show && (
        <div className={`notification-popup ${notification.type}`}>
          <div className="notification-content">
            <span className="notification-message">{notification.message}</span>
            <button 
              className="notification-close"
              onClick={() => setNotification({ show: false, message: '', type: '' })}
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Link Insertion Modal */}
      {linkModal.show && (
        <div className="modal-overlay">
          <div className="link-modal">
            <div className="modal-header">
              <h3>Insert Link</h3>
              <button 
                className="modal-close"
                onClick={() => setLinkModal({ show: false, url: '', text: '' })}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>URL:</label>
                <input
                  type="url"
                  value={linkModal.url}
                  onChange={(e) => setLinkModal({...linkModal, url: e.target.value})}
                  placeholder="https://example.com"
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label>Link Text (optional):</label>
                <input
                  type="text"
                  value={linkModal.text}
                  onChange={(e) => setLinkModal({...linkModal, text: e.target.value})}
                  placeholder="Display text for the link"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn btn-secondary"
                onClick={() => setLinkModal({ show: false, url: '', text: '' })}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary"
                onClick={insertLink}
              >
                Insert Link
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Admin Header */}
      <header className="admin-header">
        <Container>
          <div className="admin-header-content">
            <div className="admin-header-left">
              <h1>News Content Management</h1>
              <p>Manage news articles, announcements, and updates for KBH Classroom</p>
            </div>
            <div className="admin-header-right">
              <a href="/admin-dashboard" className="btn btn-back">
                ← Back to Admin Dashboard
              </a>
            </div>
          </div>
        </Container>
      </header>

      {/* Main Content */}
      <main className="admin-main">
        <Container>
          <div className="admin-content">
            <div className="admin-actions">
              <button 
                className="btn btn-primary"
                onClick={() => {
                  setShowForm(true);
                  setEditingArticle(null);
                  setSelectedFiles([]);
                  setFormData({
                    title: '',
                    excerpt: '',
                    content: '',
                    category: 'announcement',
                    author: '',
                    readTime: '',
                    images: [],
                    coverImage: '',
                    published: false
                  });
                }}
              >
                Add New Article
              </button>
            </div>

            {/* Article Form Modal */}
            {showForm && (
              <div className="modal-overlay">
                <div className="article-form-modal">
                  <div className="modal-header">
                    <h3>{editingArticle ? 'Edit Article' : 'Add New Article'}</h3>
                    <button 
                      className="modal-close"
                      onClick={() => {
                        setShowForm(false);
                        setEditingArticle(null);
                      }}
                    >
                      ×
                    </button>
                  </div>
                  <div className="modal-body">
                    <form onSubmit={handleSubmit}>
                      <div className="form-group">
                        <label>Title:</label>
                        <input
                          type="text"
                          value={formData.title}
                          onChange={(e) => setFormData({...formData, title: e.target.value})}
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label>Excerpt:</label>
                        <textarea
                          value={formData.excerpt}
                          onChange={(e) => setFormData({...formData, excerpt: e.target.value})}
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label>Content:</label>
                        <div className="quill-editor-container">
                          <div ref={editorRef} className="quill-editor"></div>
                        </div>
                        
                        {/* Remaining Images Display */}
                        {(selectedFiles.length > 0 || (editingArticle && (editingArticle.images || editingArticle.image) && (editingArticle.images?.length > 0 || editingArticle.image))) && (
                          <div className="remaining-images-section">
                            <label>Uploaded images (insert into article or they'll be added as a gallery):</label>
                            <div className="remaining-images-scroll">
                              {/* Show new uploaded files */}
                              {selectedFiles.map((file, index) => (
                                <div key={`remaining-new-${index}`} className="remaining-image-item">
                                  <img 
                                    src={file ? URL.createObjectURL(file) : ''} 
                                    alt={file.name}
                                    className="remaining-image-preview"
                                  />
                                  <small className="remaining-image-name">{file.name}</small>
                                  <div className="image-actions">
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-primary insert-image-btn"
                                      onClick={() => insertMediaIntoEditor(file)}
                                    >
                                      Insert into article
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-danger"
                                      onClick={() => showDeleteModalForNewFile(index)}
                                    >
                                      Delete
                                    </button>
                                  </div>
                                </div>
                              ))}
                              
                              {/* Show existing images from the article */}
                              {editingArticle && (editingArticle.images || editingArticle.image) && (editingArticle.images || [editingArticle.image]).map((imagePath, index) => {
                                // Extract actual filename from S3 URL
                                const actualFilename = imagePath.split('/').pop().split('-').slice(1).join('-') || imagePath.split('/').pop();
                                return (
                                  <div key={`remaining-existing-${index}`} className="remaining-image-item">
                                    <img 
                                      src={imagePath} 
                                      alt={actualFilename}
                                      className="remaining-image-preview"
                                    />
                                    <small className="remaining-image-name">{actualFilename}</small>
                                  <div className="image-actions">
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-primary insert-image-btn"
                                      onClick={() => insertExistingImageIntoEditor(imagePath)}
                                    >
                                      Insert into article
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-danger"
                                      onClick={() => showDeleteModalForExistingImage(imagePath, index)}
                                    >
                                      Delete
                                    </button>
                                  </div>
                                </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="form-row">
                        <div className="form-group">
                          <label>Category:</label>
                          <div className="category-input-container">
                            <input
                              type="text"
                              value={formData.category}
                              onChange={(e) => setFormData({...formData, category: e.target.value})}
                              placeholder="Type a category (e.g., Technology, Events, etc.)"
                              list="category-suggestions"
                              required
                            />
                            <datalist id="category-suggestions">
                              {categories.map(cat => (
                                <option key={cat._id} value={cat.displayName} />
                              ))}
                            </datalist>
                            <small className="category-help">
                              Type a new category or select from existing ones. New categories will be automatically saved.
                            </small>
                          </div>
                        </div>

                        <div className="form-group">
                          <label>Author:</label>
                          <input
                            type="text"
                            value={formData.author}
                            onChange={(e) => setFormData({...formData, author: e.target.value})}
                            required
                          />
                        </div>
                      </div>

                      <div className="form-row">
                        <div className="form-group">
                          <label>Read Time:</label>
                          <input
                            type="text"
                            value={formData.readTime}
                            onChange={(e) => setFormData({...formData, readTime: e.target.value})}
                            placeholder="e.g., 3 min read"
                            required
                          />
                        </div>

                        <div className="form-group">
                          <label>Article Media (Images & Videos):</label>
                          <DragDropFileUpload
                            onFileSelected={handleFileSelect}
                            onFileRemoved={handleFileRemove}
                            selectedFile={selectedFiles.length > 0 ? selectedFiles[0] : null}
                            accept="image/*,video/*"
                            multiple={true}
                            maxFileSize={5 * 1024 * 1024 * 1024} // 5GB
                            supportedTypes={['.jpg', '.jpeg', '.png', '.gif', '.mp4', '.avi', '.mov', '.wmv']}
                            placeholder="Click to upload media or drag and drop"
                            description="Supports: Images (JPG, PNG, GIF) and Videos (MP4, AVI, MOV, WMV) up to 5GB each"
                          />
                                                {selectedFiles.length > 0 && (
                        <div className="selected-files">
                          {selectedFiles.map((file, index) => (
                            <div key={index} className="file-item">
                              <small className="file-info">
                                {index + 1}. {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                              </small>
                              <button
                                type="button"
                                className="btn btn-sm btn-danger delete-file-btn"
                                onClick={() => deleteSelectedFile(index)}
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                          {formData.images.length > 0 && !editingArticle && selectedFiles.length === 0 && (
                            <div className="existing-images">
                              <small className="existing-images-label">Existing images:</small>
                              {formData.images.map((image, index) => (
                                <small key={index} className="file-info">
                                  {index + 1}. {image}
                                </small>
                              ))}
                            </div>
                          )}
                          {uploading && (
                            <small className="upload-status">Uploading images...</small>
                          )}
                        </div>

                        <div className="form-group">
                          <label>Cover Image:</label>
                          <div className="cover-image-section">
                            <select
                              value={formData.coverImage}
                              onChange={(e) => setFormData({...formData, coverImage: e.target.value})}
                              className="cover-image-select"
                            >
                              <option value="">Select a cover image...</option>
                              {selectedFiles.map((file, index) => (
                                <option key={`cover-${index}`} value={file.name}>
                                  {file.name}
                                </option>
                              ))}
                              {editingArticle && (editingArticle.images || editingArticle.image) && (editingArticle.images || [editingArticle.image]).map((imagePath, index) => (
                                <option key={`cover-existing-${index}`} value={imagePath}>
                                  Existing Image {index + 1}
                                </option>
                              ))}
                            </select>
                            {formData.coverImage && (
                              <div className="cover-image-preview">
                                <img 
                                  src={formData.coverImage.startsWith('http') || formData.coverImage.startsWith('/') 
                                    ? formData.coverImage 
                                    : (() => {
                                        const selectedFile = selectedFiles.find(f => f.name === formData.coverImage);
                                        if (selectedFile) {
                                          return URL.createObjectURL(selectedFile);
                                        }
                                        // If not found in selectedFiles, it might be an existing image
                                        if (editingArticle && (editingArticle.images || editingArticle.image)) {
                                          const existingImages = editingArticle.images || [editingArticle.image];
                                          if (existingImages.includes(formData.coverImage)) {
                                            return formData.coverImage;
                                          }
                                        }
                                        return '';
                                      })()}
                                  alt="Cover preview"
                                  className="cover-preview-img"
                                />
                              </div>
                            )}
                          </div>
                          <small className="cover-image-help">
                            Select one of your uploaded images to use as the article cover image
                          </small>
                        </div>
                      </div>

                      <div className="form-group">
                        <label>
                          <input
                            type="checkbox"
                            checked={formData.published}
                            onChange={(e) => setFormData({...formData, published: e.target.checked})}
                          />
                          Published
                        </label>
                      </div>
                    </form>
                  </div>
                  <div className="modal-footer">
                    <button 
                      type="button" 
                      className="btn btn-secondary"
                      onClick={() => {
                        setShowForm(false);
                        setEditingArticle(null);
                      }}
                    >
                      Cancel
                    </button>
                    <button 
                      type="button" 
                      className="btn btn-primary"
                      onClick={handleSubmit}
                    >
                      {editingArticle ? 'Update Article' : 'Add Article'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Delete Confirmation Modal */}
            {deleteModal.show && (
              <div className="modal-overlay">
                <div className="delete-confirmation-modal">
                  <div className="modal-header">
                    <h3>Confirm Delete</h3>
                  </div>
                  <div className="modal-body">
                    <p>Are you sure you want to delete the article:</p>
                    <p className="article-title">"{deleteModal.articleTitle}"</p>
                    <p className="warning-text">This action cannot be undone.</p>
                  </div>
                  <div className="modal-footer">
                    <button 
                      type="button" 
                      className="btn btn-secondary"
                      onClick={cancelDelete}
                    >
                      Cancel
                    </button>
                    <button 
                      type="button" 
                      className="btn btn-danger"
                      onClick={confirmDelete}
                    >
                      Delete Article
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Image Delete Confirmation Modal */}
            {imageDeleteModal.show && (
              <div className="modal-overlay">
                <div className="delete-confirmation-modal">
                  <div className="modal-header">
                    <h3>Confirm Image Delete</h3>
                  </div>
                  <div className="modal-body">
                    <p>Are you sure you want to delete this image?</p>
                    {imageDeleteModal.imagePath && (
                      <p className="image-name">"{imageDeleteModal.imagePath}"</p>
                    )}
                    {!imageDeleteModal.isNewFile && (
                      <p className="warning-text">This will permanently remove the image from the article and storage. This action cannot be undone.</p>
                    )}
                    {imageDeleteModal.isNewFile && (
                      <p className="info-text">This will remove the image from your upload selection.</p>
                    )}
                  </div>
                  <div className="modal-footer">
                    <button 
                      type="button" 
                      className="btn btn-secondary"
                      onClick={cancelImageDelete}
                    >
                      Cancel
                    </button>
                    <button 
                      type="button" 
                      className="btn btn-danger"
                      onClick={imageDeleteModal.isNewFile ? removeSelectedFile : deleteExistingImage}
                    >
                      {imageDeleteModal.isNewFile ? 'Remove from Selection' : 'Delete Image'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="articles-list">
              <h2>All Articles</h2>
              
              {/* Search Bar - At the Top */}
              <div className="d-flex justify-content-end mb-3">
                <form onSubmit={handleSearch} className="form-inline d-flex">
                  <input
                    className="form-control mr-2"
                    type="search"
                    name="search"
                    placeholder="Search articles..."
                    aria-label="Search"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    autoComplete="off"
                    style={{ 
                      width: '300px',
                      height: '38px',
                      fontSize: '14px'
                    }}
                  />
                  <button 
                    className="btn" 
                    type="submit"
                    style={{ 
                      height: '38px',
                      fontSize: '14px',
                      padding: '6px 12px',
                      backgroundColor: 'var(--darkPurple)',
                      color: 'var(--darkOrange)',
                      border: '1px solid var(--darkPurple)',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = 'var(--darkOrange)';
                      e.target.style.color = 'var(--darkPurple)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'var(--darkPurple)';
                      e.target.style.color = 'var(--darkOrange)';
                    }}
                  >
                    Search
                  </button>
                </form>
              </div>
              
              {/* Items Per Page Controls - Below Search */}
              <div className="row mb-3">
                <div className="col-md-6">
                  <div className="d-flex align-items-center">
                    <label htmlFor="articlesPerPage" className="form-label me-2 mb-0">Articles per page:</label>
                    <select 
                      id="articlesPerPage"
                      className="form-select"
                      value={articlesPerPage}
                      onChange={(e) => handleItemsPerPageChange(parseInt(e.target.value))}
                      style={{ 
                        maxWidth: '80px', 
                        height: '32px',
                        fontSize: '14px',
                        padding: '4px 8px'
                      }}
                    >
                      {[10, 20, 50, 100].map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="col-md-6 text-end">
                  <small style={{ color: 'var(--darkOrange)' }}>
                    Showing {((currentPage - 1) * articlesPerPage) + 1} to {Math.min(currentPage * articlesPerPage, filteredArticles.length)} of {filteredArticles.length} articles
                  </small>
                </div>
              </div>
              
              <div className="articles-grid">
                {currentArticles.map(article => (
                  <div key={article._id} className="article-card">
                    <div className="article-header">
                      <h3>{article.title}</h3>
                                          <div className="article-meta">
                      <span className={`status ${article.published ? 'published' : 'draft'}`}>
                        {article.published ? 'Published' : 'Draft'}
                      </span>
                      <span className="date">{article.date}</span>
                      {article.published && article.publishDate && (
                        <span className="publish-date">Published: {article.publishDate}</span>
                      )}
                    </div>
                    </div>
                    <p className="excerpt">{article.excerpt}</p>
                    <div className="article-footer">
                      <span className="category">{article.category}</span>
                      <span className="author">{article.author}</span>
                    </div>
                    <div className="article-actions">
                      <button 
                        className="btn btn-sm btn-primary"
                        onClick={() => handleEdit(article)}
                      >
                        Edit
                      </button>
                      <button 
                        className="btn btn-sm btn-secondary"
                        onClick={() => togglePublished(article._id)}
                      >
                        {article.published ? 'Unpublish' : 'Publish'}
                      </button>
                      <button 
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDelete(article._id)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              {/* Pagination Controls */}
              {filteredArticles.length > 0 && (
                <StandardPagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  itemsPerPage={articlesPerPage}
                  onItemsPerPageChange={handleItemsPerPageChange}
                  totalItems={filteredArticles.length}
                  itemsPerPageLabel="Articles per page:"
                  pageInfoLabel="Showing"
                  showItemsPerPage={false}
                />
              )}
            </div>
          </div>
        </Container>
      </main>
      </div>
    </div>
  );
};

export default NewsAdmin; 